import { useState, useEffect } from 'react';
import { SEOHead } from '@/components/SEOHead';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, Waves, Sun, Moon, Calendar, Tent, Palmtree, Sparkles, Sailboat, Home } from 'lucide-react';
import CabanaIcon from '@/components/icons/CabanaIcon';
import TubeIcon from '@/components/icons/TubeIcon';
import PicnicTableIcon from '@/components/icons/PicnicTableIcon';
import KayakIcon from '@/components/icons/KayakIcon';
import PoolIcon from '@/components/icons/PoolIcon';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';

// Property images
import heroAerial from '@/assets/hero-aerial.jpg';
import geronimoImage from '@/assets/properties/geronimo.jpg';
import riverRanchImage from '@/assets/properties/river-ranch.jpg';
import sonsIslandImage from '@/assets/properties/sons-island.jpg';
import rioCiboloImage from '@/assets/properties/rio-cibolo.jpg';
import blueRiverCampImage from '@/assets/properties/blue-river-camp.jpg';

type VisitType = 'day' | 'overnight' | 'both';

interface Property {
  id: string;
  name: string;
  waterway: string;
  image: string;
  dayVisitUrl?: string;
  overnightUrl?: string;
  availableOptions: VisitType[];
}

const properties: Property[] = [
  {
    id: 'sons-guadalupe',
    name: "Son's Guadalupe",
    waterway: 'Guadalupe River',
    image: heroAerial,
    dayVisitUrl: '/book-now/sons-guadalupe',
    overnightUrl: '/book-now/sons-guadalupe-overnight',
    availableOptions: ['day', 'overnight'],
  },
  {
    id: 'sons-rio-cibolo',
    name: "Son's Rio Cibolo",
    waterway: 'Cibolo Creek',
    image: rioCiboloImage,
    dayVisitUrl: '/book-now/rio-cibolo',
    overnightUrl: '/book-now/rio-cibolo-overnight',
    availableOptions: ['day', 'overnight'],
  },
  {
    id: 'sons-river-ranch',
    name: "Son's River Ranch",
    waterway: 'San Marcos River',
    image: riverRanchImage,
    dayVisitUrl: '/book-now/river-ranch',
    overnightUrl: '/book-now/river-ranch-overnight',
    availableOptions: ['day', 'overnight'],
  },
  {
    id: 'sons-blue-river-camp',
    name: "Son's Blue River Camp",
    waterway: 'San Marcos River',
    image: blueRiverCampImage,
    dayVisitUrl: '/book-now/blue-river-camp',
    overnightUrl: '/book-now/blue-river-camp-overnight',
    availableOptions: ['day', 'overnight'],
  },
  {
    id: 'sons-geronimo',
    name: "Son's Geronimo",
    waterway: 'Geronimo Creek',
    image: geronimoImage,
    overnightUrl: '/book-now/geronimo',
    availableOptions: ['overnight'],
  },
  {
    id: 'sons-island',
    name: "Son's Island",
    waterway: 'Lake McQueeney',
    image: sonsIslandImage,
    dayVisitUrl: '/book-now/sons-island',
    overnightUrl: '/book-now/sons-island',
    availableOptions: ['day'],
  },
];

const BookNow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<'properties' | 'plan'>('properties');
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const property = properties.find(p => p.id === id);
      if (property) {
        if (property.id === 'sons-geronimo') {
          navigate('/book-now/geronimo');
          return;
        }
        setSelectedProperty(property);
        setStep('plan');
      }
    } else {
      window.scrollTo(0, 0);
    }
    
    const state = location.state as { selectedPropertyId?: string } | null;
    if (state?.selectedPropertyId) {
      const propertyIdMap: Record<string, string> = {
        'rio-cibolo': 'sons-rio-cibolo',
        'river-ranch': 'sons-river-ranch',
        'blue-river-camp': 'sons-blue-river-camp',
        'geronimo': 'sons-geronimo',
        'sons-island': 'sons-island',
        'sons-guadalupe': 'sons-guadalupe',
      };
      const fullId = propertyIdMap[state.selectedPropertyId] || state.selectedPropertyId;
      const property = properties.find(p => p.id === fullId);
      if (property) {
        setSelectedProperty(property);
        setStep('plan');
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state, location.hash, navigate]);

  const handlePropertySelect = (property: Property) => {
    if (property.id === 'sons-geronimo') {
      navigate('/book-now/geronimo');
      return;
    }
    
    setSelectedProperty(property);
    setStep('plan');
  };

  const handlePlanSelect = (type: VisitType) => {
    if (!selectedProperty) return;
    
    if (selectedProperty.id === 'sons-geronimo' && (type === 'overnight' || type === 'both')) {
      navigate('/book-now/geronimo');
      return;
    }
    
    if (selectedProperty.id === 'sons-river-ranch') {
      if (type === 'day') {
        navigate('/book-now/river-ranch');
        return;
      } else if (type === 'overnight' || type === 'both') {
        navigate('/book-now/river-ranch-overnight');
        return;
      }
    }
    
    if (selectedProperty.id === 'sons-blue-river-camp') {
      if (type === 'day') {
        navigate('/book-now/blue-river-camp');
        return;
      } else if (type === 'overnight' || type === 'both') {
        navigate('/book-now/blue-river-camp-overnight');
        return;
      }
    }
    
    if (selectedProperty.id === 'sons-rio-cibolo') {
      if (type === 'day') {
        navigate('/book-now/rio-cibolo');
        return;
      } else if (type === 'overnight' || type === 'both') {
        navigate('/book-now/rio-cibolo-overnight');
        return;
      }
    }
    
    if (selectedProperty.id === 'sons-island') {
      navigate('/book-now/sons-island');
      return;
    }
    
    if (selectedProperty.id === 'sons-guadalupe') {
      if (type === 'day') {
        navigate('/book-now/sons-guadalupe');
        return;
      } else if (type === 'overnight' || type === 'both') {
        navigate('/book-now/sons-guadalupe-overnight');
        return;
      }
    }
  };

  const handleBackToProperties = () => {
    setSelectedProperty(null);
    setStep('properties');
  };

  return (
    <>
      <SEOHead
        title="Book Cabanas, Tubing & Glamping | Son's Guadalupe"
        description="Reserve your private cabana, tubing float, or glamping tent on the Guadalupe River in New Braunfels. Summer weekends fill fast — book now!"
        canonical="/book-now"
      />

      <AnimatePresence mode="wait">
        {step === 'properties' && (
          <motion.div
            key="properties"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen"
          >
            <div className="relative min-h-screen">
              <div className="fixed inset-0 -z-10">
                <img
                  src={heroAerial}
                  alt="Son's Island"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40" />
              </div>

              <Header />

              <div className="container max-w-7xl mx-auto px-4 pt-28 pb-16">
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <h1 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wide">
                    Select a Property to Visit
                  </h1>
                  <svg
                    viewBox="0 0 200 12"
                    className="w-48 md:w-64 lg:w-96 h-3 mx-auto mt-3"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M2 8 Q 15 2, 30 8 T 58 8 T 86 8 T 114 8 T 142 8 T 170 8 T 198 8"
                      fill="none"
                      stroke="#12b6da"
                      strokeWidth="3"
                      strokeLinecap="round"
                      className="drop-shadow-[0_2px_4px_rgba(18,182,218,0.4)]"
                    />
                  </svg>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {properties.map((property, index) => (
                    <motion.div
                      key={property.id}
                      id={property.id}
                      onClick={() => handlePropertySelect(property)}
                      initial={{ opacity: 0, y: 40 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="group relative aspect-[4/5] rounded-2xl overflow-hidden cursor-pointer shadow-xl scroll-mt-32"
                    >
                      <img
                        src={property.image}
                        alt={property.name}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500" />
                      
                      {property.id === 'sons-guadalupe' && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
                          <div className="bg-accent text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                            <Home className="w-4 h-4" />
                            You Are Here
                          </div>
                        </div>
                      )}
                      
                      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                        <p className="text-sm uppercase tracking-widest font-medium mb-2 flex items-center gap-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" style={{ color: '#12b6da' }}>
                          <Waves className="w-4 h-4" />
                          {property.waterway}
                        </p>
                        <h3 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wide mb-4">
                          {property.name}
                        </h3>
                        <div className="flex items-center gap-2 text-white/80 group-hover:text-white transition-colors">
                          <span className="text-sm uppercase tracking-widest">Select</span>
                          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-2" />
                        </div>
                      </div>

                      <div className="absolute inset-0 border-4 border-transparent group-hover:border-accent/30 rounded-2xl transition-colors duration-500" />
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-primary py-16">
              <div className="container max-w-4xl mx-auto px-6 text-center">
                <h2 className="font-display text-2xl md:text-4xl font-bold text-primary-foreground uppercase tracking-wide mb-4">
                  Need Help Choosing?
                </h2>
                <p className="text-primary-foreground/80 mb-8 text-lg">
                  Compare all properties side-by-side to find your perfect match.
                </p>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary uppercase tracking-widest font-bold rounded-full px-8"
                  asChild
                >
                  <a href="/why-sons">Compare Properties</a>
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {step === 'plan' && selectedProperty && (
          <motion.div
            key="plan"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen relative"
          >
            <div className="fixed inset-0 -z-10">
              <img
                src={selectedProperty.image}
                alt={selectedProperty.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/60" />
            </div>

            <Header />

            <div className="relative z-10 min-h-screen px-4 pt-20 pb-24 md:pt-28 md:pb-16 md:px-6 flex flex-col md:items-center md:justify-start">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="w-full max-w-2xl"
              >
                <div className="text-center mb-8 md:mb-12">
                  <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.25 }}
                    className="font-display text-2xl md:text-4xl font-bold text-white uppercase tracking-wide mb-2"
                  >
                    {selectedProperty.name}
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
                    style={{ color: '#12b6da' }}
                  >
                    <Waves className="w-4 h-4" />
                    {selectedProperty.waterway}
                  </motion.p>
                  <motion.h1 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-wide"
                  >
                    What's Your
                    <br />
                    <span className="text-accent">Plan?</span>
                  </motion.h1>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.45 }}
                    className="mt-4 inline-flex items-center gap-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-2"
                  >
                    <span className="text-accent text-lg">✦</span>
                    <p className="text-white/90 text-xs md:text-sm">
                      {selectedProperty.id === 'sons-island' 
                        ? "Pro Tip: Many People choose to stay overnight at our nearby properties: Son's Rio Cibolo & Son's Geronimo and visit Son's Island for the day."
                        : <>Pro Tip: Staying overnight too? Reserve your cabin or glamping rental first & then add on cabanas & kayaking for <span className="text-white font-medium">25% OFF!</span></>
                      }
                    </p>
                  </motion.div>
                </div>

                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className={`flex flex-col md:grid gap-4 ${
                    selectedProperty.availableOptions.length === 1 
                      ? 'md:grid-cols-1 max-w-xs mx-auto' 
                      : selectedProperty.availableOptions.length === 2
                        ? 'md:grid-cols-2 max-w-lg mx-auto'
                        : 'md:grid-cols-3'
                  }`}
                >
                  {selectedProperty.availableOptions.includes('day') && (
                    <motion.button
                      onClick={() => handlePlanSelect('day')}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="relative group bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 md:p-8 pt-8 md:pt-10 pb-8 md:pb-10 text-center hover:bg-white/20 hover:border-white/40 transition-all duration-300 order-1"
                    >
                      {selectedProperty.id === 'sons-island' && (
                        <div className="absolute top-1 md:-top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full whitespace-nowrap shadow-lg">
                          Most Popular
                        </div>
                      )}
                      <div className="flex flex-col items-center gap-3 md:gap-4">
                        <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full bg-yellow-400/20 flex items-center justify-center ${selectedProperty.id === 'sons-island' ? 'mt-4 md:mt-0' : ''}`}>
                          <Sun className="w-6 h-6 md:w-8 md:h-8 text-yellow-300" />
                        </div>
                        <h3 className="font-display text-lg md:text-xl font-bold text-white uppercase tracking-wide">
                          Day Visit Only
                        </h3>
                        <p className="text-white/90 text-xs md:text-sm font-medium tracking-wide">
                          <span className="block md:inline">Rent Cabanas & Picnic Tables</span>
                          <span className="hidden md:inline"> · </span>
                          <span className="block md:inline">
                            {['sons-river-ranch', 'sons-blue-river-camp', 'sons-guadalupe'].includes(selectedProperty.id) 
                              ? 'Go Tubing & Kayaking' 
                              : 'Go Kayaking & Paddleboarding'}
                          </span>
                        </p>
                      </div>
                    </motion.button>
                  )}

                  {selectedProperty.availableOptions.includes('overnight') && (
                    <motion.button
                      onClick={() => handlePlanSelect('overnight')}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`relative group bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 md:p-8 ${selectedProperty.id !== 'sons-island' ? 'pt-8 md:pt-10 pb-8 md:pb-10' : ''} text-center hover:bg-white/20 hover:border-white/40 transition-all duration-300 order-2`}
                    >
                      {selectedProperty.id !== 'sons-island' && (
                        <div className="absolute top-1 md:-top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full whitespace-nowrap shadow-lg">
                          Most Popular
                        </div>
                      )}
                      <div className="flex flex-col items-center gap-3 md:gap-4">
                        <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/20 flex items-center justify-center ${selectedProperty.id !== 'sons-island' ? 'mt-4 md:mt-0' : ''}`}>
                          <Moon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                        </div>
                        <h3 className="font-display text-lg md:text-xl font-bold text-white uppercase tracking-wide">
                          Stay Overnight Too
                        </h3>
                        <p className="text-white/90 text-xs md:text-sm font-medium tracking-wide">
                          {selectedProperty.id === 'sons-guadalupe' && 'Glamping Tents'}
                          {selectedProperty.id === 'sons-river-ranch' && 'Reserve Safari & Glamping Cabins · Bell Tents & Tent Camping Spots'}
                          {selectedProperty.id === 'sons-blue-river-camp' && 'Reserve Log Cabins · Glamping Cabins · Bell Tents & Tent Camping'}
                          {selectedProperty.id === 'sons-geronimo' && 'Birdhouse Cabins'}
                          {selectedProperty.id === 'sons-rio-cibolo' && 'Reserve Waterfront, Safari & Glamping Cabins & Bell Tents'}
                          {selectedProperty.id === 'sons-island' && 'Reserve Lakefront Tent Camping'}
                        </p>
                        {selectedProperty.id !== 'sons-island' && (
                          <>
                            <div className="bg-accent text-white text-[10px] md:text-xs font-bold uppercase tracking-wide px-4 py-1.5 rounded-lg shadow-[0_0_15px_rgba(255,255,255,0.5),0_0_30px_rgba(var(--accent)/0.4)] mt-1 text-center leading-tight">
                              <span className="block">Stay Overnight & Get 25% Off</span>
                              <span className="block">Day Rentals*</span>
                            </div>
                            <p className="text-white/70 text-[11px] md:text-xs mt-2 italic">*At time of Booking Only</p>
                          </>
                        )}
                      </div>
                    </motion.button>
                  )}
                </motion.div>

                {/* Property Feature Sections */}
                {selectedProperty.id === 'sons-river-ranch' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.55 }}
                    className="mt-6 md:mt-10"
                  >
                    <h2 className="font-display text-lg md:text-2xl font-bold text-white uppercase tracking-wide text-center mb-4 md:mb-6">
                      Why Son's River Ranch?
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                      {[
                        { icon: Waves, label: '3/4 Mile of Riverfront' },
                        { icon: CabanaIcon, label: 'Riverside Cabanas' },
                        { icon: TubeIcon, label: 'Tube & Kayak' },
                        { icon: Tent, label: 'Glamp or Camp' },
                      ].map((feature, index) => (
                        <motion.div
                          key={feature.label}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                          className="flex flex-col items-center text-center"
                        >
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center mb-1 md:mb-2">
                            <feature.icon className="w-5 h-5 md:w-8 md:h-8 text-white" />
                          </div>
                          <span className="text-white/90 text-xs md:text-sm font-medium">{feature.label}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {selectedProperty.id === 'sons-blue-river-camp' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.55 }}
                    className="mt-6 md:mt-10"
                  >
                    <h2 className="font-display text-lg md:text-2xl font-bold text-white uppercase tracking-wide text-center mb-4 md:mb-6">
                      Why Son's Blue River Camp?
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                      {[
                        { icon: Palmtree, label: 'Riverfront Beach' },
                        { icon: CabanaIcon, label: '2-Story Cabanas' },
                        { icon: TubeIcon, label: 'Tube & Kayak' },
                        { icon: Home, label: 'Log Cabins' },
                      ].map((feature, index) => (
                        <motion.div
                          key={feature.label}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                          className="flex flex-col items-center text-center"
                        >
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center mb-1 md:mb-2">
                            <feature.icon className="w-5 h-5 md:w-8 md:h-8 text-white" />
                          </div>
                          <span className="text-white/90 text-xs md:text-sm font-medium">{feature.label}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {selectedProperty.id === 'sons-rio-cibolo' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.55 }}
                    className="mt-6 md:mt-10"
                  >
                    <h2 className="font-display text-lg md:text-2xl font-bold text-white uppercase tracking-wide text-center mb-4 md:mb-6">
                      Why Son's Rio Cibolo?
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                      {[
                        { icon: Home, label: 'Waterfront Cabins' },
                        { icon: CabanaIcon, label: 'Creekside Cabanas' },
                        { icon: PoolIcon, label: 'Pools and Gameroom' },
                        { icon: KayakIcon, label: 'Kayak and Paddleboard' },
                      ].map((feature, index) => (
                        <motion.div
                          key={feature.label}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                          className="flex flex-col items-center text-center"
                        >
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center mb-1 md:mb-2">
                            <feature.icon className="w-5 h-5 md:w-8 md:h-8 text-white" />
                          </div>
                          <span className="text-white/90 text-xs md:text-sm font-medium">{feature.label}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {selectedProperty.id === 'sons-island' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.55 }}
                    className="mt-6 md:mt-10"
                  >
                    <h2 className="font-display text-lg md:text-2xl font-bold text-white uppercase tracking-wide text-center mb-4 md:mb-6">
                      Why Son's Island?
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                      {[
                        { icon: Sailboat, label: 'Lake Access' },
                        { icon: Palmtree, label: 'Tropical Vibes' },
                        { icon: CabanaIcon, label: 'Private Cabanas' },
                        { icon: Sparkles, label: 'Island Paradise' },
                      ].map((feature, index) => (
                        <motion.div
                          key={feature.label}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                          className="flex flex-col items-center text-center"
                        >
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center mb-1 md:mb-2">
                            <feature.icon className="w-5 h-5 md:w-8 md:h-8 text-white" />
                          </div>
                          <span className="text-white/90 text-xs md:text-sm font-medium">{feature.label}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {selectedProperty.id === 'sons-guadalupe' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.55 }}
                    className="mt-6 md:mt-10"
                  >
                    <h2 className="font-display text-lg md:text-2xl font-bold text-white uppercase tracking-wide text-center mb-4 md:mb-6">
                      Why Son's Guadalupe?
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                      {[
                        { icon: PicnicTableIcon, label: 'In-Water Picnic Tables' },
                        { icon: TubeIcon, label: 'Tube the Guadalupe' },
                        { icon: CabanaIcon, label: 'Private Cabanas' },
                        { icon: Tent, label: 'Glamp on the River' },
                      ].map((feature, index) => (
                        <motion.div
                          key={feature.label}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                          className="flex flex-col items-center text-center"
                        >
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center mb-1 md:mb-2">
                            <feature.icon className="w-5 h-5 md:w-8 md:h-8 text-white" />
                          </div>
                          <span className="text-white/90 text-xs md:text-sm font-medium">{feature.label}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {selectedProperty.id === 'sons-geronimo' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.55 }}
                    className="mt-6 md:mt-10"
                  >
                    <h2 className="font-display text-lg md:text-2xl font-bold text-white uppercase tracking-wide text-center mb-4 md:mb-6">
                      Why Son's Geronimo?
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                      {[
                        { icon: Waves, label: 'Creekside Retreat' },
                        { icon: Home, label: 'Luxury Lodging' },
                        { icon: PoolIcon, label: 'Pools and Gameroom' },
                        { icon: KayakIcon, label: 'Kayaking' },
                      ].map((feature, index) => (
                        <motion.div
                          key={feature.label}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                          className="flex flex-col items-center text-center"
                        >
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center mb-1 md:mb-2">
                            <feature.icon className="w-5 h-5 md:w-8 md:h-8 text-white" />
                          </div>
                          <span className="text-white/90 text-xs md:text-sm font-medium">{feature.label}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BookNow;
